import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { api } from '../lib/api.js';

/**
 * Sign-in, first-run setup, invite acceptance and post-signup passkey
 * enrolment — the four views of the original login.html, driven by the same
 * endpoints. Passkey ceremonies run through @simplewebauthn/browser against the
 * server's @simplewebauthn/server options.
 */
type View = 'signin' | 'setup' | 'invite' | 'invite-bad' | 'enrol';

export function LoginPage({ needsSetup }: { needsSetup: boolean }) {
  const qc = useQueryClient();
  // Invite tokens ride in the URL FRAGMENT so they never appear in a request
  // line or server log; the old ?invite= query form is still read for links
  // minted before the change.
  const inviteToken =
    new URLSearchParams(window.location.hash.replace(/^#/, '')).get('invite') ??
    new URLSearchParams(window.location.search).get('invite');
  const [view, setView] = useState<View>(needsSetup ? 'setup' : 'signin');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const inviteCheck = useQuery({
    queryKey: ['invite-check', inviteToken],
    queryFn: () =>
      api.post<{ valid: boolean; label: string | null }>('/api/auth/invite-check', {
        token: inviteToken!,
      }),
    enabled: !!inviteToken && !needsSetup,
  });
  useEffect(() => {
    if (inviteToken && inviteCheck.data) {
      setView(inviteCheck.data.valid ? 'invite' : 'invite-bad');
    }
  }, [inviteToken, inviteCheck.data]);

  const finish = () => {
    window.history.replaceState(null, '', '/');
    void qc.invalidateQueries({ queryKey: ['auth-status'] });
  };

  async function run(fn: () => Promise<void>) {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const passkeySignIn = () =>
    run(async () => {
      const opts = await api.post<{ challengeId: string; publicKey: never }>(
        '/api/auth/webauthn/login-options',
      );
      const credential = await startAuthentication({ optionsJSON: opts.publicKey });
      await api.post('/api/auth/webauthn/login-verify', { challengeId: opts.challengeId, credential });
      finish();
    });

  const passwordSignIn = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      await api.post('/api/auth/login', { username: f.get('username'), password: f.get('password') });
      finish();
    });
  };

  const doSetup = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      await api.post('/api/auth/setup', {
        username: f.get('username'),
        password: f.get('password'),
        rep: f.get('rep') || undefined,
      });
      setView('enrol');
    });
  };

  const acceptInvite = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      await api.post('/api/auth/invite-accept', {
        token: inviteToken,
        username: f.get('username'),
        password: f.get('password'),
        rep: f.get('rep') || undefined,
      });
      setView('enrol');
    });
  };

  const enrolPasskey = () =>
    run(async () => {
      const opts = await api.post<{ challengeId: string; publicKey: never }>(
        '/api/auth/webauthn/register-options',
      );
      const credential = await startRegistration({ optionsJSON: opts.publicKey });
      await api.post('/api/auth/webauthn/register-verify', {
        challengeId: opts.challengeId,
        credential,
        label: 'created at sign-up',
      });
      finish();
    });

  const titles: Record<View, [string, string]> = {
    signin: ['Traffic', 'Publisher stats. Sign in to continue.'],
    setup: ['Welcome', 'Create the first account to set up this dashboard.'],
    invite: ['You’re invited', inviteCheck.data?.label ? `Invite: ${inviteCheck.data.label}` : 'Create your account.'],
    'invite-bad': ['Traffic', 'This invite link is invalid, already used, or expired — ask for a fresh one.'],
    enrol: ['Almost done', 'Add a passkey so future sign-ins are one tap.'],
  };
  const [title, subtitle] = titles[view];

  return (
    <div className="login-wrap">
      <div className="login-split">
        <img className="login-poster" src="/pidgeon-poster.jpg" alt="The Pidgons Are Trained" />
        <div className="login-card">
        <h1>{title}</h1>
        <p className="sub">{subtitle}</p>
        <p className="form-err" role="alert">
          {error}
        </p>

        {view === 'signin' || view === 'invite-bad' ? (
          <>
            <button className="wide" onClick={() => void passkeySignIn()} disabled={busy}>
              Sign in with passkey
            </button>
            <div className="divider">or</div>
            <form onSubmit={passwordSignIn}>
              <label>
                Username
                <input name="username" autoComplete="username" required />
              </label>
              <label>
                Password
                <input name="password" type="password" autoComplete="current-password" required />
              </label>
              <button className="wide" type="submit" disabled={busy}>
                Sign in with password
              </button>
            </form>
          </>
        ) : null}

        {view === 'setup' ? (
          <form onSubmit={doSetup}>
            <label>
              Username
              <input name="username" autoComplete="username" required />
            </label>
            <label>
              Password (8+ characters)
              <input name="password" type="password" autoComplete="new-password" minLength={8} required />
            </label>
            <label>
              Rep name (optional)
              <input name="rep" placeholder="defaults to username" />
            </label>
            <button className="wide" type="submit" disabled={busy}>
              Create account
            </button>
          </form>
        ) : null}

        {view === 'invite' ? (
          <form onSubmit={acceptInvite}>
            <label>
              Username
              <input name="username" autoComplete="username" required />
            </label>
            <label>
              Password (8+ characters)
              <input name="password" type="password" autoComplete="new-password" minLength={8} required />
            </label>
            <label>
              Rep name (optional)
              <input name="rep" placeholder="defaults to username" />
            </label>
            <button className="wide" type="submit" disabled={busy}>
              Create my account
            </button>
            <p className="hint">
              You’ll set up a passkey next — the password is the fallback for a lost device.
            </p>
          </form>
        ) : null}

        {view === 'enrol' ? (
          <>
            <button className="wide" onClick={() => void enrolPasskey()} disabled={busy}>
              Set up my passkey
            </button>
            <p className="hint">
              Your account is ready. A passkey (Face ID / Touch ID / security key) makes sign-in one
              tap. <a href="/" onClick={(e) => (e.preventDefault(), finish())}>Skip for now</a>
            </p>
          </>
        ) : null}
        </div>
      </div>
    </div>
  );
}
